import { useCallback, useMemo } from "react";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { bulletsToSqafActionItems, bulletsToSqafInsightCards } from "@/utils/mistralInsightMappers";
import { buildSqafSchoolMarkdown } from "@/utils/sqaafMistralInsights";
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Download,
  LineChart,
  RefreshCw,
  School,
  Search,
  Star,
  TrendingDown,
  TrendingUp,
  User,
} from "lucide-react";
import { fmt2, getManagementLabel } from "@/lib/sqaafLabels";
import { toTitleCase } from "@/utils/toTitleCase";
import {
  buildProfileChartBars,
  buildQuestionRows,
  buildSchoolActionQueue,
  buildSchoolHeroCopy,
  buildSchoolHeroMetrics,
  buildSchoolInsightCards,
  findSelectedSchool,
  getBandTag,
  truncateSchoolName,
} from "@/utils/sqaafSchoolDerived";

const BAND_OPTIONS = ["", "Critical", "Needs Focus", "Developing", "Strong", "Exemplary"];
const SORT_OPTIONS = [
  { value: "Answered_%", label: "Answered %" },
  { value: "SQAAF_Score_%", label: "SQAAF %" },
  { value: "school_name", label: "School name" },
  { value: "block", label: "Block" },
];

export default function SqafSchoolDiagnostics({
  overview,
  schoolsList,
  schoolDetail,
  selectedSchoolCode,
  showSkeleton,
  refreshing,
  blockFilter,
  managementFilter,
  schoolSearch,
  schoolBand,
  schoolSort,
  schoolPage,
  schoolTotalPages,
  schoolExportLoading,
  onRefresh,
  onExport,
  onSearchChange,
  onBandChange,
  onSortChange,
  onSchoolSelect,
  onSearchSubmit,
  onPageChange,
}) {
  const schools = schoolsList?.schools || [];
  const selectedSchool = useMemo(
    () => findSelectedSchool(schools, selectedSchoolCode),
    [schools, selectedSchoolCode]
  );
  const safeSchool = useMemo(() => selectedSchool ?? {}, [selectedSchool]);
  const detailSchoolCode = schoolDetail?.school?.school_code;
  const detailMatchesSelection =
    Boolean(selectedSchoolCode && detailSchoolCode) && detailSchoolCode === selectedSchoolCode;

  const hasData = Boolean(safeSchool.school_code || schoolDetail?.school);
  const buildLocalMarkdown = useCallback(
    () => buildSqafSchoolMarkdown({ overview, school: safeSchool, schoolDetail }),
    [overview, safeSchool, schoolDetail]
  );
  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        code: safeSchool.school_code,
        score: safeSchool["SQAAF_Score_%"],
        detail: schoolDetail?.gap_to_district_mean,
      }),
    [safeSchool.school_code, safeSchool["SQAAF_Score_%"], schoolDetail?.gap_to_district_mean]
  );
  const {
    loading: aiLoading,
    pickFromBucket,
    pickHeadline,
    pickPrimaryAction,
    pickMapped,
    isMistral,
  } = useMistralNarrativeOverlay({
    kind: "sqaaf-schools",
    title: "SQAAF School Diagnostics",
    buildLocalMarkdown,
    context: { overview, school: safeSchool, schoolDetail },
    enabled: hasData,
    scopeKey,
  });

  const localHero = useMemo(
    () => buildSchoolHeroCopy(safeSchool, schoolDetail, overview),
    [safeSchool, schoolDetail, overview]
  );
  const hero = useMemo(() => {
    if (aiLoading) return localHero;
    return {
      ...localHero,
      headline: pickHeadline(localHero.headline),
      body: pickFromBucket(localHero.body, "insights", 0) || localHero.body,
      signal: pickFromBucket(localHero.signal, "root", 0) || localHero.signal,
      chips: isMistral
        ? [{ label: "Mistral AI Assisted", className: "purple" }, ...localHero.chips]
        : localHero.chips,
    };
  }, [aiLoading, localHero, pickHeadline, pickFromBucket, isMistral]);

  const heroMetrics = useMemo(
    () => buildSchoolHeroMetrics(overview, safeSchool, schoolDetail),
    [overview, safeSchool, schoolDetail]
  );
  const profileBars = useMemo(
    () => buildProfileChartBars(schoolDetail?.section_profile),
    [schoolDetail?.section_profile]
  );
  const localInsightCards = useMemo(() => buildSchoolInsightCards(schoolDetail), [schoolDetail]);
  const insightCards = useMemo(
    () => (aiLoading ? localInsightCards : pickMapped(localInsightCards, "insights", bulletsToSqafInsightCards)),
    [aiLoading, pickMapped, localInsightCards]
  );
  const localActionQueue = useMemo(
    () => buildSchoolActionQueue(safeSchool, schoolDetail),
    [safeSchool, schoolDetail]
  );
  const actionQueue = useMemo(() => {
    if (aiLoading) return localActionQueue;
    const mappedItems = pickMapped(localActionQueue.items, "actions", bulletsToSqafActionItems);
    return mappedItems?.length ? { ...localActionQueue, items: mappedItems } : localActionQueue;
  }, [aiLoading, pickMapped, localActionQueue]);
  const questionRows = useMemo(
    () => buildQuestionRows(schoolDetail?.lowest_10_questions),
    [schoolDetail?.lowest_10_questions]
  );

  const detailSchool = detailMatchesSelection ? schoolDetail.school : safeSchool;
  const detailBand = detailSchool.Band || "Developing";
  const blockLabel = blockFilter || "All";
  const bandLabel = schoolBand || "All Bands";
  const mgmtLabel = managementFilter ? getManagementLabel(managementFilter) : "All";

  if (showSkeleton && !overview) {
    return <div className="sq-exec-skeleton" data-testid="sqaf-school-skeleton" />;
  }

  return (
    <>
      <section className="page-header" id="diagnostics">
        <div>
          <div className="eyebrow">
            <School className="svg-icon" strokeWidth={2} />
            School diagnostics · Pune District · 2025–26
          </div>
          <h1>School Diagnostics</h1>
          <p>
            Find schools, compare school-level section performance against block and district benchmarks, and convert
            lowest-scoring questions into targeted action.
          </p>
        </div>
        <div className="header-actions">
          <button type="button" className="ghost-action" onClick={onExport} disabled={schoolExportLoading}>
            <Download className="svg-icon" strokeWidth={2} />
            {schoolExportLoading ? "Exporting…" : "Download CSV"}
          </button>
          <button type="button" className="primary-action" onClick={onRefresh} disabled={refreshing}>
            <RefreshCw className={`svg-icon ${refreshing ? "animate-spin" : ""}`} strokeWidth={2} />
            Refresh
          </button>
        </div>
      </section>

      <section className="scope-card sq-school-scope" aria-label="School diagnostics filters">
        <div className="filter-row">
          <span className="filter-chip">
            Scope <b>Pune District</b>
          </span>
          <span className="filter-chip">
            Block <b>{blockLabel}</b>
          </span>
          <span className="filter-chip">
            Band <b>{bandLabel}</b>
          </span>
          <span className="filter-chip">
            Management <b>{mgmtLabel}</b>
          </span>
          <label className="search-box">
            <Search className="svg-icon" strokeWidth={2} />
            <input
              placeholder="Search by school name or code..."
              value={schoolSearch}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onSearchSubmit?.()}
              aria-label="Search schools"
            />
          </label>
        </div>
        <div className="scope-note">
          Active: {Number(overview?.n_schools || 0).toLocaleString()} schools · {schools.length} visible · MongoDB
          source
        </div>
      </section>

      <section className="hero-summary">
        <div className="hero-layout">
          <div>
            <div className="score-focus">
              <div className="gauge" style={hero.gaugeStyle}>
                <div>
                  <strong>{hero.score}</strong>
                  <span>SQAAF %</span>
                </div>
              </div>
              <div className="score-copy">
                <h2 className="dashboard-kpi-hero-title">{toTitleCase(hero.headline)}</h2>
                <p>{hero.body}</p>
                <div className="status-row">
                  {hero.chips.map((chip) => (
                    <span key={chip.label} className={`status-chip ${chip.className}`.trim()}>
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="summary-note">
              <div className="icon-box orange">
                <TrendingUp className="svg-icon" strokeWidth={2} />
              </div>
              <div>
                <b>Diagnostic signal</b>
                <span>{hero.signal}</span>
              </div>
            </div>
          </div>

          <div className="metric-grid">
            {heroMetrics.map((metric) => (
              <article key={metric.label} className="metric-card">
                <div className="metric-head">
                  <small>{metric.label}</small>
                  <span className={`mini-badge ${metric.badge}`.trim()}>
                    {metric.badge === "green" && <CheckCircle2 className="svg-icon" strokeWidth={2} />}
                    {metric.badge === "red" && <AlertTriangle className="svg-icon" strokeWidth={2} />}
                    {metric.badge === "orange" && <TrendingDown className="svg-icon" strokeWidth={2} />}
                    {metric.badge === "purple" && <Star className="svg-icon" strokeWidth={2} />}
                    {!metric.badge && <School className="svg-icon" strokeWidth={2} />}
                  </span>
                </div>
                <strong>{metric.value}</strong>
                <span>{metric.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="panel table-card">
        <div className="table-head">
          <div>
            <h3>Schools</h3>
            <p>
              Use search, band and block filters to identify schools needing drill-down review. The highlighted row is
              opened below.
            </p>
          </div>
          <div className="table-tools">
            <label className="select-chip">
              <select
                className="sq-inline-select"
                value={schoolBand || ""}
                onChange={(e) => onBandChange(e.target.value)}
                aria-label="Filter by band"
              >
                {BAND_OPTIONS.map((b) => (
                  <option key={b || "all"} value={b}>
                    {b || "All bands"}
                  </option>
                ))}
              </select>
            </label>
            <label className="select-chip">
              <select
                className="sq-inline-select"
                value={schoolSort}
                onChange={(e) => onSortChange(e.target.value)}
                aria-label="Sort schools"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="ghost-action" onClick={onExport} disabled={schoolExportLoading}>
              {schoolExportLoading ? "Exporting…" : "Export filtered view"}
            </button>
          </div>
        </div>
        <div className="table-wrap sq-school-table-wrap">
          <table>
            <thead>
              <tr>
                <th>School Code</th>
                <th>School Name</th>
                <th>Block</th>
                <th>Cluster</th>
                <th>Management</th>
                <th>Category</th>
                <th>Answered %</th>
                <th>SQAAF %</th>
                <th>Band</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {schools.length === 0 ? (
                <tr>
                  <td colSpan={10}>No schools match the current filters.</td>
                </tr>
              ) : (
                schools.map((s, index) => {
                  const isSelected = selectedSchoolCode === s.school_code;
                  const bandTag = getBandTag(s.Band);
                  const rowKey = s.school_code || `school-row-${index}`;
                  return (
                    <tr
                      key={rowKey}
                      className={isSelected ? "selected" : ""}
                      onClick={() => onSchoolSelect(s.school_code)}
                    >
                      <td>
                        <b>{s.school_code}</b>
                      </td>
                      <td>{truncateSchoolName(s.school_name)}</td>
                      <td>{s.block || "—"}</td>
                      <td>{s.cluster || "—"}</td>
                      <td>{s.school_management || "—"}</td>
                      <td>{s.school_category || "—"}</td>
                      <td>{fmt2(s["Answered_%"])}%</td>
                      <td className="score">{fmt2(s["SQAAF_Score_%"])}</td>
                      <td>
                        <span className={`tag ${bandTag}`}>{s.Band}</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="text-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSchoolSelect(s.school_code);
                          }}
                        >
                          Detail
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {schoolsList?.total > 50 ? (
          <div className="sq-school-pagination">
            <span>
              Showing {schools.length} of {schoolsList.total} · Page {schoolPage + 1} of {schoolTotalPages}
            </span>
            <div className="sq-school-pagination-actions">
              <button type="button" className="ghost-action" disabled={schoolPage === 0} onClick={() => onPageChange(schoolPage - 1)}>
                Previous
              </button>
              <button
                type="button"
                className="ghost-action"
                disabled={(schoolPage + 1) * 50 >= schoolsList.total}
                onClick={() => onPageChange(schoolPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </section>

      {detailMatchesSelection ? (
        <>
          <div className="section-title">
            <div>
              <h2>Selected School Diagnostic Profile</h2>
              <p>
                The detail card separates benchmark comparison, low questions and action ownership so the page
                supports decisions instead of only displaying rows.
              </p>
            </div>
            <a className="section-action" href="#lowest-questions">
              View lowest questions →
            </a>
          </div>

          <section className="detail-grid">
            <article className="panel selected-school">
              <div className="school-title">
                <div>
                  <h3>{toTitleCase((detailSchool.school_name || "").toUpperCase())}</h3>
                  <p>
                    Section score profile vs block and district. Blue bars show the selected school. Green/gray bars
                    provide benchmark context.
                  </p>
                </div>
                <span className={`tag ${getBandTag(detailBand)}`}>{detailBand}</span>
              </div>
              <div className="school-meta">
                <div className="meta-card">
                  <small>School Code</small>
                  <b>{detailSchool.school_code || "—"}</b>
                </div>
                <div className="meta-card">
                  <small>Block / Cluster</small>
                  <b>
                    {detailSchool.block || "—"}
                    {detailSchool.cluster ? ` / ${detailSchool.cluster}` : ""}
                  </b>
                </div>
                <div className="meta-card">
                  <small>Answered</small>
                  <b>{fmt2(detailSchool["Answered_%"])}%</b>
                </div>
                <div className="meta-card">
                  <small>SQAAF Score</small>
                  <b>{fmt2(detailSchool["SQAAF_Score_%"])}%</b>
                </div>
              </div>
              <div className="chart-surface">
                <div className="profile-chart" aria-label="Section score profile">
                  {profileBars.map((bar) => (
                    <div key={bar.key} className="bar-group" title={bar.title}>
                      <span className="bar block" style={{ height: `${bar.block}%` }} />
                      <span className="bar district" style={{ height: `${bar.district}%` }} />
                      <span className="bar school" style={{ height: `${bar.school}%` }} />
                      <small>{bar.section}</small>
                    </div>
                  ))}
                </div>
                <div className="legend-row">
                  <span className="legend-pill">
                    <i className="dot block" /> Block mean
                  </span>
                  <span className="legend-pill">
                    <i className="dot district" /> District mean
                  </span>
                  <span className="legend-pill">
                    <i className="dot school" /> School
                  </span>
                </div>
              </div>
            </article>

            <aside className="side-stack">
              {insightCards.map((card, index) => (
                <article key={`${card.title}-${index}`} className="insight-card">
                  <div className={`icon-box ${card.tone}`}>
                    {card.tone === "red" && <AlertTriangle className="svg-icon" strokeWidth={2} />}
                    {card.tone === "green" && <CheckCircle2 className="svg-icon" strokeWidth={2} />}
                  </div>
                  <h3>{toTitleCase(card.title)}</h3>
                  <p>{card.body}</p>
                </article>
              ))}
              <article className="panel">
                <div className="panel-head">
                  <div>
                    <h3>Recommended Action Queue</h3>
                    <p>Targeted steps for the selected school.</p>
                  </div>
                  <span className="pill">{actionQueue.pill}</span>
                </div>
                <div className="action-stack">
                  {actionQueue.items.map((item, index) => (
                    <div key={`${item.title}-${index}`} className="action-item">
                      <div className={`icon-box ${item.tone}`}>
                        {item.title.includes("resources") && <BookOpen className="svg-icon" strokeWidth={2} />}
                        {item.title.includes("mentor") && <User className="svg-icon" strokeWidth={2} />}
                        {item.title.includes("cycle") && <LineChart className="svg-icon" strokeWidth={2} />}
                      </div>
                      <div>
                        <h4>{item.title}</h4>
                        <p>{item.body}</p>
                      </div>
                      <span className={`priority ${item.priorityClass}`.trim()}>{item.priority}</span>
                    </div>
                  ))}
                </div>
              </article>
            </aside>
          </section>

          <section className="panel table-card question-table" id="lowest-questions">
            <div className="table-head">
              <div>
                <h3>Lowest 10 Answered Questions</h3>
                <p>
                  Question-level backlog for the selected school. Use this as the working list for verification and
                  remediation.
                </p>
              </div>
              <div className="table-tools">
                <span className="select-chip">Score ≤ 25%</span>
                <button type="button" className="ghost-action">
                  Create action list
                </button>
              </div>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Question</th>
                    <th>Score</th>
                    <th>Score %</th>
                    <th>Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {questionRows.length === 0 ? (
                    <tr>
                      <td colSpan={4}>No low-scoring answered questions for this school.</td>
                    </tr>
                  ) : (
                    questionRows.map((row, index) => (
                      <tr key={`${row.code}-${index}-${row.question}`}>
                        <td>
                          <b>{row.code}</b> {row.question}
                        </td>
                        <td>{row.score}</td>
                        <td className="score-bad">{row.scorePct}</td>
                        <td>
                          <span className={`tag ${row.priorityTag}`}>{row.priority}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}
    </>
  );
}
